import type { api } from '@udm/contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { canAny } from '../../api/client';
import {
  acknowledgeAlarm,
  closeAlarm,
  fetchAlarmPage,
  fetchAlarmSummary,
  fetchAlarmTypes,
  type AlarmFilter,
} from '../../api/ops';
import { useSiteScope } from '../../app/scope';
import { usePresenceCounters } from '../../realtime/aggregates';
import { BarChart } from '../../ui/charts';
import { Card, Drawer, Field, Kpi, PageHeader, SeverityBadge } from '../../ui/components';
import { formatDateTime, formatNumber, formatRelative } from '../../ui/format';
import { useMe } from '../auth/session';

const STATES = ['active', 'open', 'acknowledged', 'resolved', 'all'] as const;
const CATEGORIES = [
  'luminaire',
  'power_supply',
  'electrical',
  'communication',
  'environment',
  'device',
  'operation',
];

/**
 * Alarm centre (AFM requirement d): summary, filters, history with keyset pagination, acknowledge / close with a note,
 * detail with the detectors that fired and their raw values. The list refreshes when the realtime alarm counter
 * changes (debounced) — never a global device refetch.
 */
export function AlarmsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useMe().data;
  const siteId = useSiteScope((s) => s.siteId);
  const [state, setState] = useState<(typeof STATES)[number]>('active');
  const [severity, setSeverity] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<api.Alarm | null>(null);
  const filter: AlarmFilter = {
    state,
    severity,
    category,
    type,
    q: search,
    site_id: siteId ?? undefined,
    limit: 100,
  };
  const summary = useQuery({
    queryKey: ['alarms', 'summary', siteId],
    queryFn: () => fetchAlarmSummary(siteId),
  });
  const types = useQuery({
    queryKey: ['alarm-types', ''],
    queryFn: () => fetchAlarmTypes(null),
    staleTime: 5 * 60_000,
  });
  const pages = useInfiniteQuery({
    queryKey: ['alarms', 'page', filter],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => fetchAlarmPage({ ...filter, before: pageParam }),
    getNextPageParam: (last) => last.next ?? undefined,
  });
  // Realtime: the live alarm counter changes when a device's alarm severity changes → refresh this page's lists.
  const liveCount = usePresenceCounters((s) => s.alarms);
  useEffect(() => {
    const id = setTimeout(() => void qc.invalidateQueries({ queryKey: ['alarms'] }), 3000);
    return () => clearTimeout(id);
  }, [liveCount, qc]);
  const items = pages.data?.pages.flatMap((p) => p.items) ?? [];
  const total = pages.data?.pages[0]?.total ?? 0;
  const typeName = (key: string, en: string) =>
    t(`alarmTypes.${key}.name` as 'alarmTypes.device_offline.name', { defaultValue: en });
  const s = summary.data;
  const daily = useMemo(
    () =>
      (s?.daily ?? []).map((d) => ({
        label: d.day.slice(8),
        value: d.opened,
        title: `${d.day}: +${d.opened} / −${d.resolved}`,
      })),
    [s],
  );
  return (
    <div className="page">
      <PageHeader title={t('alarms.title')} subtitle={t('common.count', { count: total })} />
      <div className="kpi-row" style={{ marginBottom: 'var(--space-4)' }}>
        <Kpi label={t('alarmCenter.active')} value={formatNumber(s?.active)} />
        <Kpi
          label={t('severity.critical')}
          value={formatNumber(s?.by_severity.critical ?? 0)}
          tone="critical"
        />
        <Kpi label={t('severity.warning')} value={formatNumber(s?.by_severity.warning ?? 0)} />
        <Kpi label={t('severity.info')} value={formatNumber(s?.by_severity.info ?? 0)} />
        <Kpi label={t('alarmCenter.unacknowledged')} value={formatNumber(s?.unacknowledged)} />
        <Kpi
          label={t('alarmCenter.opened24h')}
          value={`${formatNumber(s?.opened_24h)} / ${formatNumber(s?.resolved_24h)}`}
        />
      </div>
      <div className="grid-2" style={{ marginBottom: 'var(--space-4)' }}>
        <Card title={t('alarmCenter.trend')}>
          <BarChart data={daily} ariaLabel={t('alarmCenter.trend')} height={150} />
        </Card>
        <Card title={t('alarmCenter.topTypes')} bodyClass="">
          <table className="table table-tight">
            <tbody>
              {(s?.top_types ?? []).map((x) => (
                <tr
                  key={x.alarm_type}
                  className="row-click"
                  onClick={() => (setType(x.alarm_type), setState('active'))}
                >
                  <td>{typeName(x.alarm_type, x.name_en)}</td>
                  <td className="num">{formatNumber(x.count)}</td>
                </tr>
              ))}
              {s?.top_types.length === 0 && (
                <tr>
                  <td className="muted">{t('alarmCenter.none')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>
      <Card bodyClass="">
        <div className="card-body filter-bar inline-form">
          <Field label={t('common.status')}>
            <select
              className="input"
              value={state}
              onChange={(e) => setState(e.target.value as typeof state)}
            >
              {STATES.map((x) => (
                <option key={x} value={x}>
                  {t(`alarmCenter.state.${x}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('alarms.severity')}>
            <select className="input" value={severity} onChange={(e) => setSeverity(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {(['critical', 'warning', 'info'] as const).map((x) => (
                <option key={x} value={x}>
                  {t(`severity.${x}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('alarmCenter.category')}>
            <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {CATEGORIES.map((x) => (
                <option key={x} value={x}>
                  {t(`alarmCategory.${x}` as 'alarmCategory.device')}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('common.type')}>
            <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {(types.data?.items ?? []).map((x) => (
                <option key={x.key} value={x.key}>
                  {typeName(x.key, x.name_en)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('common.search')}>
            <input
              className="input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('alarms.device')}
            />
          </Field>
        </div>
        <div className="ops-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>{t('alarms.severity')}</th>
                <th>{t('common.type')}</th>
                <th>{t('alarms.device')}</th>
                <th className="hide-mobile">{t('common.site')}</th>
                <th>{t('common.status')}</th>
                <th>{t('alarms.opened')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr
                  key={a.id}
                  className={`row-click${selected?.id === a.id ? ' row-selected' : ''}`}
                  onClick={() => setSelected(a)}
                >
                  <td>
                    <SeverityBadge severity={a.severity} />
                  </td>
                  <td>{typeName(a.alarm_type, a.type_name_en)}</td>
                  <td>
                    <Link to={`/devices/${a.device_id}`} onClick={(e) => e.stopPropagation()}>
                      {a.device_name}
                    </Link>
                  </td>
                  <td className="muted hide-mobile">{a.site_name}</td>
                  <td>
                    {t(`alarms.state.${a.state}`)}
                    {a.resolution && (
                      <span className="cell-sub">
                        {t(`alarmCenter.resolution.${a.resolution}` as 'alarmCenter.resolution.cleared', {
                          defaultValue: a.resolution,
                        })}
                      </span>
                    )}
                  </td>
                  <td className="muted nowrap" title={formatDateTime(a.opened_at)}>
                    {formatRelative(a.opened_at)}
                  </td>
                </tr>
              ))}
              {!pages.isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    {t('alarmCenter.none')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {pages.hasNextPage && (
          <div className="card-body">
            <button
              type="button"
              className="btn"
              disabled={pages.isFetchingNextPage}
              onClick={() => void pages.fetchNextPage()}
            >
              {t('alarmCenter.more')}
            </button>
          </div>
        )}
      </Card>
      {selected && (
        <AlarmDrawer
          alarm={selected}
          canAck={canAny(me, 'alarm.acknowledge')}
          canClose={canAny(me, 'alarm.manage')}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            void qc.invalidateQueries({ queryKey: ['alarms'] });
          }}
          typeName={typeName(selected.alarm_type, selected.type_name_en)}
        />
      )}
    </div>
  );
}

function AlarmDrawer({
  alarm,
  canAck,
  canClose,
  onClose,
  onChanged,
  typeName,
}: {
  alarm: api.Alarm;
  canAck: boolean;
  canClose: boolean;
  onClose: () => void;
  onChanged: () => void;
  typeName: string;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const ack = useMutation({
    mutationFn: () => acknowledgeAlarm(alarm.id, note || undefined),
    onSuccess: onChanged,
  });
  const close = useMutation({
    mutationFn: () => closeAlarm(alarm.id, note || undefined),
    onSuccess: onChanged,
  });
  const faults = (alarm.details.faults as Record<string, unknown>[] | undefined) ?? [];
  const failed = (ack.error ?? close.error) as Error | null;
  return (
    <Drawer
      title={typeName}
      subtitle={alarm.device_name}
      onClose={onClose}
      footer={
        alarm.state !== 'resolved' && (canAck || canClose) ? (
          <div className="ops-stack" style={{ width: '100%' }}>
            <input
              className="input"
              placeholder={t('alarmCenter.note')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={1000}
            />
            <div className="toolbar">
              {canAck && alarm.state === 'open' && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={ack.isPending}
                  onClick={() => ack.mutate()}
                >
                  {t('alarmCenter.acknowledge')}
                </button>
              )}
              {canClose && (
                <button
                  type="button"
                  className="btn"
                  disabled={close.isPending}
                  onClick={() => close.mutate()}
                >
                  {t('alarmCenter.closeManual')}
                </button>
              )}
            </div>
            {failed && (
              <span className="form-error">
                {t(`errors.${failed.message}` as 'errors.generic', { defaultValue: failed.message })}
              </span>
            )}
          </div>
        ) : undefined
      }
    >
      <dl className="kv">
        <dt>{t('alarms.severity')}</dt>
        <dd>
          <SeverityBadge severity={alarm.severity} />
        </dd>
        <dt>{t('alarmCenter.category')}</dt>
        <dd>
          {t(`alarmCategory.${alarm.category}` as 'alarmCategory.device', { defaultValue: alarm.category })}
        </dd>
        <dt>{t('alarms.device')}</dt>
        <dd>
          <Link to={`/devices/${alarm.device_id}`}>{alarm.device_name}</Link>
        </dd>
        <dt>{t('common.site')}</dt>
        <dd>{alarm.site_name ?? '—'}</dd>
        <dt>{t('alarms.opened')}</dt>
        <dd>{formatDateTime(alarm.opened_at)}</dd>
        <dt>{t('alarmCenter.lastActive')}</dt>
        <dd>{formatDateTime(alarm.last_active_at)}</dd>
        <dt>{t('common.status')}</dt>
        <dd>{t(`alarms.state.${alarm.state}`)}</dd>
        {alarm.acknowledged_at && (
          <>
            <dt>{t('alarmCenter.acknowledged')}</dt>
            <dd>
              {formatDateTime(alarm.acknowledged_at)} · {alarm.acknowledged_by ?? '—'}
              {alarm.ack_note && <span className="cell-sub">{alarm.ack_note}</span>}
            </dd>
          </>
        )}
        {alarm.resolved_at && (
          <>
            <dt>{t('alarmCenter.resolved')}</dt>
            <dd>
              {formatDateTime(alarm.resolved_at)} ·{' '}
              {t(`alarmCenter.resolution.${alarm.resolution}` as 'alarmCenter.resolution.cleared', {
                defaultValue: alarm.resolution ?? '',
              })}
            </dd>
          </>
        )}
      </dl>
      <h4>{t('alarmCenter.detectors')}</h4>
      <p className="muted small">{t('alarmCenter.detectorsHint')}</p>
      <ul className="metric-list">
        {faults.map((f, i) => (
          <li key={i}>
            <span className="mono">{String(f.detector)}</span>
            <span className="muted small mono"> {JSON.stringify(f.value ?? null)}</span>
            {f.threshold != null && (
              <span className="muted small">
                {' '}
                · {t('alarmCenter.threshold', { value: String(f.threshold) })}
              </span>
            )}
          </li>
        ))}
      </ul>
    </Drawer>
  );
}
